import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Table, Card, Form, Button } from 'react-bootstrap';
import { getIncomeStatementData, syncGoogleSheets, getAvailableSegments } from '../api'; // New API call
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Link } from 'react-router-dom';
import CostAllocationModal from './CostAllocationModal'; // NUEVO: Modal de proporciones

const IncomeStatement = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedSegment, setSelectedSegment] = useState('');
  const [availableSegments, setAvailableSegments] = useState([]);
  const [showExpensesDetail, setShowExpensesDetail] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null); // NUEVO: Para el segundo nivel de abanico
  const [showAllocationModal, setShowAllocationModal] = useState(false); // NUEVO: Control del modal de proporciones

  const [syncing, setSyncing] = useState(false);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getIncomeStatementData(selectedYear, selectedSegment);
      setReportData(data);
    } catch (error) {
      console.error("Error fetching income statement data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedSegment]);

  const fetchSegments = useCallback(async () => {
    try {
      const segments = await getAvailableSegments();
      setAvailableSegments(segments || []);
    } catch (error) {
      console.error("Error fetching segments:", error);
    }
  }, []);

  useEffect(() => {
    fetchSegments();
  }, [fetchSegments]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleSync = async () => {
    if (!window.confirm("¿Estás seguro de que deseas sincronizar con Google Sheets? Esto puede tomar unos segundos.")) return;
    
    setSyncing(true);
    try {
      const result = await syncGoogleSheets();
      alert(result.message || "Sincronización completada exitosamente.");
      fetchReportData(); // Refresh data after sync
    } catch (error) {
      console.error("Sync error:", error);
      alert("Error al sincronizar. Revisa la consola o intenta nuevamente.");
    } finally {
      setSyncing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value || 0);
  };

  const formatPercentage = (value, total) => {
    if (!total || total === 0) return '';
    const percentage = (value / total) * 100;
    return `${percentage.toFixed(1)}%`;
  };

  // Modified renderCell to support ID-based drill-down with column context
  const renderCell = (value, totalIncome, ids = [], eerrColumn = null) => {
    const safeValue = value || 0;
    const safeTotalIncome = totalIncome || 0;

    const content = (
      <div className="d-flex flex-column align-items-end">
        <span>{formatCurrency(safeValue)}</span>
        {safeTotalIncome !== 0 && safeValue !== 0 && (
          <span className="text-muted" style={{ fontSize: '0.75rem', lineHeight: '1' }}>
            {formatPercentage(safeValue, safeTotalIncome)}
          </span>
        )}
      </div>
    );

    // If we have specific IDs, link to them directly with context
    if (ids && ids.length > 0) {
      let url = `/?ids=${ids.join(',')}`;
      if (eerrColumn) {
          url += `&eerrColumn=${eerrColumn}`;
      }
      return (
        <Link to={url} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
          {content}
        </Link>
      );
    }

    return content;
  };

  // Totales Anuales - calculated from fetched reportData
  const annualTotals = useMemo(() => {
    const totals = {};
    if (reportData.length > 0) {
      Object.keys(reportData[0]).forEach(key => {
        if (typeof reportData[0][key] === 'number') {
          totals[key] = reportData.reduce((sum, month) => sum + (month[key] || 0), 0);
        }
        // Accumulate IDs for totals column
        if (key.endsWith('Ids') && Array.isArray(reportData[0][key])) {
             totals[key] = reportData.reduce((acc, month) => [...acc, ...(month[key] || [])], []);
        }
      });
      // Asegurar que bonoProduccion se sume explícitamente
      totals['bonoProduccion'] = reportData.reduce((sum, month) => sum + (month.bonoProduccion || 0), 0);
      totals['bonoProduccionIds'] = reportData.reduce((acc, month) => [...acc, ...(month.bonoProduccionIds || [])], []);
    }
    return totals;
  }, [reportData]);

  // Obtener todas las categorías únicas de gastos de todos los meses
  const expenseCategories = useMemo(() => {
    const categories = new Set();
    reportData.forEach(month => {
      if (month.gastosGeneralesBreakdown) {
        Object.keys(month.gastosGeneralesBreakdown).forEach(cat => categories.add(cat));
      }
    });
    return Array.from(categories).sort();
  }, [reportData]);


  if (loading && !reportData.length) return <Container className="py-4 text-center">Cargando Estado de Resultados...</Container>;

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Estado de Resultados (EERR)</h2>
        <div className="d-flex align-items-center gap-3">
            <button 
                className="btn btn-success" 
                onClick={handleSync} 
                disabled={syncing}
            >
                {syncing ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sincronizando...
                    </>
                ) : (
                    <>
                        <i className="bi bi-google me-2"></i>
                        Sincronizar Google Sheets
                    </>
                )}
            </button>

            <div className="d-flex align-items-center gap-2">
                <Form.Label className="m-0 text-muted small fw-bold">Segmento:</Form.Label>
                <Form.Select 
                    value={selectedSegment} 
                    onChange={(e) => setSelectedSegment(e.target.value)} 
                    style={{width: '180px'}}
                >
                    <option value="">Todos</option>
                    {availableSegments.map(seg => (
                        <option key={seg} value={seg}>{seg}</option>
                    ))}
                </Form.Select>
            </div>

            <Button variant="outline-primary" className="fw-bold" onClick={() => setShowAllocationModal(true)}>
                ⚙️ Proporciones
            </Button>

            <Form.Select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(parseInt(e.target.value))} 
                style={{width: '120px'}}
            >
                <option value={new Date().getFullYear() - 1}>{new Date().getFullYear() - 1}</option>
                <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
                <option value={new Date().getFullYear() + 1}>{new Date().getFullYear() + 1}</option>
            </Form.Select>
            <Link to="/" className="btn btn-outline-secondary">Volver</Link>
        </div>
      </div>

      {/* Gráfico de Tendencia */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
            <Card.Title>Tendencia Mensual de Rentabilidad</Card.Title>
            <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={reportData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="monthName" />
                        <YAxis tickFormatter={(val) => `$${val/1000}k`} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <ReferenceLine y={0} stroke="#000" />
                        <Line type="monotone" dataKey="totalIngresosVentas" name="Ventas" stroke="#82ca9d" strokeWidth={3} dot={{ r: 6 }} activeDot={{ r: 8 }} />
                        <Line type="monotone" dataKey="costoVentasDirecto" name="Costo Venta" stroke="#ffc658" strokeWidth={2} />
                        <Line type="monotone" dataKey="gastosOperacionales" name="Gastos Op." stroke="#ff8042" strokeWidth={2} />
                        <Line type="monotone" dataKey="utilidadNeta" name="Utilidad Neta" stroke="#8884d8" strokeWidth={4} dot={{ r: 6 }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </Card.Body>
      </Card>

      {/* Tabla EERR */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
            <div className="table-responsive">
                <Table bordered hover size="sm" className="mb-0 text-end align-middle">
                    <thead className="bg-light text-center">
                        <tr>
                            <th className="text-start">Concepto</th>
                            {reportData.map(d => <th key={d.monthIndex}>{d.monthName.slice(0,3)}</th>)}
                            <th className="bg-white border-start">TOTAL {selectedYear}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* INGRESOS */}
                        <tr className="table-success fw-bold">
                            <td className="text-start">Total Ingresos Operacionales</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.totalIngresosVentas, d.totalIngresosVentas)}</td>)}
                            <td className="border-start">{renderCell(annualTotals.totalIngresosVentas, annualTotals.totalIngresosVentas)}</td>
                        </tr>
                        <tr>
                            <td className="text-start ps-4 text-muted small">Ingresos por Ventas (Externas)</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.ingresosVentaExterna, d.totalIngresosVentas)}</td>)}
                            <td className="border-start">{renderCell(annualTotals.ingresosVentaExterna, annualTotals.totalIngresosVentas)}</td>
                        </tr>

                        {/* COSTO PROVEEDOR (EXTERNO) - MOVED HERE */}
                        <tr className="text-danger">
                            <td className="text-start ps-4 text-muted small">(-) Costo Proveedor (Externo)</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.costoProveedorExterna, d.totalIngresosVentas)}</td>)}
                            <td className="border-start">{renderCell(annualTotals.costoProveedorExterna, annualTotals.totalIngresosVentas)}</td>
                        </tr>

                        {/* UTILIDAD BRUTA */}
                        <tr className="table-warning fw-bold">
                            <td className="text-start">(=) Utilidad Bruta</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.utilidadBruta, d.totalIngresosVentas)}</td>)}
                            <td className="border-start">{renderCell(annualTotals.utilidadBruta, annualTotals.totalIngresosVentas)}</td>
                        </tr>

                        {/* DETALLE REMUNERACIONES */}
                        <tr>
                            <td className="text-start ps-4 text-muted small">Remuneraciones</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.costoSalarios, d.totalIngresosVentas, d.costoSalariosIds, 'costoSalarios')}</td>)}
                            <td className="border-start">{renderCell(annualTotals.costoSalarios, annualTotals.totalIngresosVentas, annualTotals.costoSalariosIds, 'costoSalarios')}</td>
                        </tr>
                        <tr>
                            <td className="text-start ps-4 text-muted small">HHEE</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.costoHHEE, d.totalIngresosVentas, d.costoHHEEIds, 'costoHHEE')}</td>)}
                            <td className="border-start">{renderCell(annualTotals.costoHHEE, annualTotals.totalIngresosVentas, annualTotals.costoHHEEIds, 'costoHHEE')}</td>
                        </tr>
                        <tr>
                            <td className="text-start ps-4 text-muted small">HHEE Sábado</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.costoHHEESabado, d.totalIngresosVentas, d.costoHHEESabadoIds, 'costoHHEESabado')}</td>)}
                            <td className="border-start">{renderCell(annualTotals.costoHHEESabado, annualTotals.totalIngresosVentas, annualTotals.costoHHEESabadoIds, 'costoHHEESabado')}</td>
                        </tr>
                        <tr>
                            <td className="text-start ps-4 text-muted small">HHEE Domingo</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.costoHHEEDomingo, d.totalIngresosVentas, d.costoHHEEDomingoIds, 'costoHHEEDomingo')}</td>)}
                            <td className="border-start">{renderCell(annualTotals.costoHHEEDomingo, annualTotals.totalIngresosVentas, annualTotals.costoHHEEDomingoIds, 'costoHHEEDomingo')}</td>
                        </tr>
                        <tr>
                            <td className="text-start ps-4 text-muted small">Bono</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.costoBono, d.totalIngresosVentas, d.costoBonoIds, 'costoBono')}</td>)}
                            <td className="border-start">{renderCell(annualTotals.costoBono, annualTotals.totalIngresosVentas, annualTotals.costoBonoIds, 'costoBono')}</td>
                        </tr>
                        <tr>
                            <td className="text-start ps-4 text-muted small">Previred</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.costoPrevired, d.totalIngresosVentas, d.costoPreviredIds, 'costoPrevired')}</td>)}
                            <td className="border-start">{renderCell(annualTotals.costoPrevired, annualTotals.totalIngresosVentas, annualTotals.costoPreviredIds, 'costoPrevired')}</td>
                        </tr>
                        <tr>
                            <td className="text-start ps-4 text-muted small">Viáticos</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.costoViaticos, d.totalIngresosVentas, d.costoViaticosIds, 'costoViaticos')}</td>)}
                            <td className="border-start">{renderCell(annualTotals.costoViaticos, annualTotals.totalIngresosVentas, annualTotals.costoViaticosIds, 'costoViaticos')}</td>
                        </tr>

                        {/* TOTAL REMUNERACIONES */}
                        <tr className="fw-bold text-danger">
                            <td className="text-start">(-) Total Remuneraciones</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.totalRemuneraciones, d.totalIngresosVentas)}</td>)}
                            <td className="border-start">{renderCell(annualTotals.totalRemuneraciones, annualTotals.totalIngresosVentas)}</td>
                        </tr>
                        

                        {/* MARGEN OPERACIONAL */}
                        <tr className="table-secondary fw-bold">
                            <td className="text-start">(=) Margen Operacional</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.margenOperacional, d.totalIngresosVentas)}</td>)}
                            <td className="border-start">{renderCell(annualTotals.margenOperacional, annualTotals.totalIngresosVentas)}</td>
                        </tr>

                        {/* OTROS INGRESOS */}
                        <tr className="fw-bold">
                            <td className="text-start">(+) Otros Ingresos</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.otrosIngresos, d.totalIngresosVentas)}</td>)}
                            <td className="border-start">{renderCell(annualTotals.otrosIngresos, annualTotals.totalIngresosVentas)}</td>
                        </tr>
                        <tr>
                            <td className="text-start ps-4 text-muted small">Intereses Financieros (DAP)</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.ingresosDap, d.totalIngresosVentas, d.ingresosDapIds, 'ingresosDap')}</td>)}
                            <td className="border-start">{renderCell(annualTotals.ingresosDap, annualTotals.totalIngresosVentas, annualTotals.ingresosDapIds, 'ingresosDap')}</td>
                        </tr>

                        {/* GASTOS OPERACIONALES - NIVEL 1 */}
                        <tr className="fw-bold text-warning" style={{cursor: 'pointer'}} onClick={() => setShowExpensesDetail(!showExpensesDetail)}>
                            <td className="text-start">
                                {showExpensesDetail ? '▼' : '▶'} (-) Gastos Operacionales
                            </td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.gastosOperacionales, d.totalIngresosVentas)}</td>)}
                            <td className="border-start">{renderCell(annualTotals.gastosOperacionales, annualTotals.totalIngresosVentas)}</td>
                        </tr>

                        {showExpensesDetail ? (
                            <>
                                {expenseCategories.map(category => {
                                    const isCategoryExpanded = expandedCategory === category;
                                    
                                    // Obtener subcategorías únicas para esta categoría de todos los meses
                                    const subcategories = new Set();
                                    reportData.forEach(month => {
                                        const bk = month.gastosGeneralesBreakdown?.[category]?.subbreakdown;
                                        if (bk) Object.keys(bk).forEach(scat => subcategories.add(scat));
                                    });
                                    const subcategoryList = Array.from(subcategories).sort();

                                    return (
                                        <React.Fragment key={category}>
                                            {/* NIVEL 2: CATEGORÍA (ej: Gasto, Remesas) */}
                                            <tr 
                                                className="bg-light" 
                                                style={{cursor: subcategoryList.length > 0 ? 'pointer' : 'default'}} 
                                                onClick={() => subcategoryList.length > 0 && setExpandedCategory(isCategoryExpanded ? null : category)}
                                            >
                                                <td className="text-start ps-5 text-muted small">
                                                    {subcategoryList.length > 0 && (isCategoryExpanded ? '▼ ' : '▶ ')}
                                                    {category}
                                                </td>
                                                {reportData.map(month => {
                                                    const detail = month.gastosGeneralesBreakdown?.[category] || { amount: 0, ids: [] };
                                                    return <td key={month.monthIndex}>{renderCell(detail.amount, month.totalIngresosVentas, detail.ids, 'gastosGenerales')}</td>;
                                                })}
                                                <td className="border-start fw-bold">
                                                    {renderCell(
                                                        reportData.reduce((sum, m) => sum + (m.gastosGeneralesBreakdown?.[category]?.amount || 0), 0),
                                                        annualTotals.totalIngresosVentas
                                                    )}
                                                </td>
                                            </tr>

                                            {/* NIVEL 3: SUBCATEGORÍAS (ej: Cafetería, Arriendo) */}
                                            {isCategoryExpanded && subcategoryList.map(subcat => (
                                                <tr key={subcat} className="bg-white border-bottom" style={{fontSize: '0.85rem'}}>
                                                    <td className="text-start ps-5 ms-4 text-muted italic" style={{paddingLeft: '4rem'}}>
                                                        └ {subcat}
                                                    </td>
                                                    {reportData.map(month => {
                                                        const sdetail = month.gastosGeneralesBreakdown?.[category]?.subbreakdown?.[subcat] || { amount: 0, ids: [] };
                                                        return <td key={month.monthIndex}>{renderCell(sdetail.amount, month.totalIngresosVentas, sdetail.ids, 'gastosGenerales')}</td>;
                                                    })}
                                                    <td className="border-start text-muted">
                                                        {renderCell(
                                                            reportData.reduce((sum, m) => sum + (m.gastosGeneralesBreakdown?.[category]?.subbreakdown?.[subcat]?.amount || 0), 0),
                                                            annualTotals.totalIngresosVentas
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                                <tr>
                                    <td className="text-start ps-5 text-muted small">Comisiones Bancarias / T/C</td>
                                    {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.comisionesBancarias, d.totalIngresosVentas, d.comisionesBancariasIds, 'comisionesBancarias')}</td>)}
                                    <td className="border-start">{renderCell(annualTotals.comisionesBancarias, annualTotals.totalIngresosVentas, annualTotals.comisionesBancariasIds, 'comisionesBancarias')}</td>
                                </tr>
                            </>
                        ) : (
                            <>
                                <tr>
                                    <td className="text-start ps-4 text-muted small">Gastos Generales</td>
                                    {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.gastosGenerales, d.totalIngresosVentas, d.gastosGeneralesIds, 'gastosGenerales')}</td>)}
                                    <td className="border-start">{renderCell(annualTotals.gastosGenerales, annualTotals.totalIngresosVentas, annualTotals.gastosGeneralesIds, 'gastosGenerales')}</td>
                                </tr>
                                <tr>
                                    <td className="text-start ps-4 text-muted small">Comisiones Bancarias / T/C</td>
                                    {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.comisionesBancarias, d.totalIngresosVentas, d.comisionesBancariasIds, 'comisionesBancarias')}</td>)}
                                    <td className="border-start">{renderCell(annualTotals.comisionesBancarias, annualTotals.totalIngresosVentas, annualTotals.comisionesBancariasIds, 'comisionesBancarias')}</td>
                                </tr>
                            </>
                        )}

                        <tr>
                            <td className="text-start ps-4 text-muted small">Castigo Deuda Incobrable</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.castigoIncobrablesFinal, d.totalIngresosVentas, d.castigoIncobrablesIds, 'castigoIncobrables')}</td>)}
                            <td className="border-start">{renderCell(annualTotals.castigoIncobrablesFinal, annualTotals.totalIngresosVentas, annualTotals.castigoIncobrablesIds, 'castigoIncobrables')}</td>
                        </tr>
                        

                        {/* EBITDA */}
                        <tr className="table-info fw-bold">
                            <td className="text-start">(=) EBITDA (Utilidad Op.)</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.ebitda, d.totalIngresosVentas)}</td>)}
                            <td className="border-start">{renderCell(annualTotals.ebitda, annualTotals.totalIngresosVentas)}</td>
                        </tr>

                        {/* IMPUESTOS */}
                        <tr>
                            <td className="text-start text-danger">(-) Impuestos Directos</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.impuestosFinal, d.totalIngresosVentas, d.impuestosDirectosIds, 'impuestosDirectos')}</td>)}
                            <td className="border-start">{renderCell(annualTotals.impuestosFinal, annualTotals.totalIngresosVentas, annualTotals.impuestosDirectosIds, 'impuestosDirectos')}</td>
                        </tr>

                        {/* UTILIDAD NETA */}
                        <tr className="table-primary fw-bold fs-5">
                            <td className="text-start">(=) Utilidad Neta</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.utilidadNeta, d.totalIngresosVentas)}</td>)}
                            <td className="border-start">{renderCell(annualTotals.utilidadNeta, annualTotals.totalIngresosVentas)}</td>
                        </tr>

                        {/* --- REPARTO DE UTILIDAD (BONO PRODUCCION) --- */}
                        <tr className="table-info">
                            <td className="text-start ps-4 fw-bold">(-) Bono Producción</td>
                            {reportData.map(d => <td key={d.monthIndex}>{renderCell(d.bonoProduccion || 0, d.totalIngresosVentas, d.bonoProduccionIds || [], 'costoBono')}</td>)}
                            <td className="border-start fw-bold bg-info text-white">{renderCell(annualTotals.bonoProduccion || 0, annualTotals.totalIngresosVentas)}</td>
                        </tr>

                        <tr className="fw-black bg-primary text-white" style={{ fontSize: '1.2rem' }}>
                            <td className="text-start ps-4">UTILIDAD DISPONIBLE</td>
                            {reportData.map(d => {
                                const finalUtil = (d.utilidadNeta || 0) - (d.bonoProduccion || 0);
                                return <td key={d.monthIndex}>{renderCell(finalUtil, d.totalIngresosVentas)}</td>;
                            })}
                            <td className="border-start fw-bold">{renderCell((annualTotals.utilidadNeta || 0) - (annualTotals.bonoProduccion || 0), annualTotals.totalIngresosVentas)}</td>
                        </tr>
                    </tbody>
                </Table>
      <CostAllocationModal show={showAllocationModal} handleClose={() => setShowAllocationModal(false)} year={selectedYear} month={reportData.length > 0 ? reportData[0].monthIndex + 1 : 1} segments={availableSegments} />            </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default IncomeStatement;
